export const FORM_VAT_RATES = [
  { value: '0', label: '0 %' },
  { value: '5.5', label: '5,5 %' },
  { value: '10', label: '10 %' },
  { value: '20', label: '20 %' },
];

export function parseQuestionRequired(raw) {
  if (raw === false || raw === 'false' || raw === 0) return false;
  return true;
}

export function parseAddValueToTotalTTC(raw) {
  return raw === true || raw === 'true' || raw === 1 || raw === '1';
}

export function questionAddsValueToTotalTTC(q) {
  if (q?.type !== 'text' || !parseAddValueToTotalTTC(q.addValueToTotalTTC)) {
    return false;
  }
  const kind = parseTextKind(q.textKind);
  return kind === 'short' || kind === 'number';
}

export const FORM_TEXT_KINDS = ['short', 'long', 'number', 'email', 'date', 'phone'];

export function parseTextKind(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (FORM_TEXT_KINDS.includes(s)) return s;
  return 'short';
}

export function normalizeVatRate(raw) {
  const s = String(raw ?? '20').replace(',', '.');
  const match = FORM_VAT_RATES.find((r) => r.value === s || Number(r.value) === Number(s));
  return match ? match.value : '20';
}

export function roundPrice(n) {
  return Math.round(n * 100) / 100;
}

export function computePricePair(htAmount, vatRateStr) {
  if (htAmount === '' || htAmount == null) {
    return { priceHT: null, priceTTC: null };
  }
  const ht = Number(String(htAmount).replace(',', '.'));
  if (!Number.isFinite(ht) || ht < 0) {
    return { priceHT: null, priceTTC: null };
  }
  const vat = Number(normalizeVatRate(vatRateStr)) || 0;
  const priceHT = roundPrice(ht);
  const priceTTC = roundPrice(ht * (1 + vat / 100));
  return { priceHT, priceTTC };
}

export function inferVatRateFromPair(priceHT, priceTTC) {
  const ht = Number(priceHT);
  const ttc = Number(priceTTC);
  if (!Number.isFinite(ht) || ht <= 0 || !Number.isFinite(ttc)) return '0';
  const implied = ((ttc / ht) - 1) * 100;
  let best = FORM_VAT_RATES[0];
  let bestDiff = Infinity;
  for (const r of FORM_VAT_RATES) {
    const diff = Math.abs(Number(r.value) - implied);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = r;
    }
  }
  return best.value;
}

export function priceFieldsFromApi(priceHT, priceTTC, legacyPrice) {
  const htRaw = priceHT != null && priceHT !== '' ? Number(priceHT) : null;
  const ttcRaw =
    priceTTC != null && priceTTC !== ''
      ? Number(priceTTC)
      : legacyPrice != null && legacyPrice !== ''
        ? Number(legacyPrice)
        : null;
  if (htRaw != null && Number.isFinite(htRaw)) {
    return {
      priceAmount: String(htRaw),
      priceVatRate: inferVatRateFromPair(htRaw, ttcRaw ?? htRaw),
    };
  }
  if (ttcRaw != null && Number.isFinite(ttcRaw)) {
    const vat = '20';
    const htApprox = roundPrice(ttcRaw / (1 + Number(vat) / 100));
    return { priceAmount: String(htApprox), priceVatRate: vat };
  }
  return { priceAmount: '', priceVatRate: '20' };
}

export function effectiveOptionPriceTTC(opt) {
  if (opt?.priceTTC != null && opt?.priceTTC !== '') {
    const n = Number(opt.priceTTC);
    return Number.isFinite(n) ? n : null;
  }
  if (opt?.price != null && opt?.price !== '') {
    const n = Number(opt.price);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function emptyOption() {
  return {
    optionName: '',
    priceAmount: '',
    priceVatRate: '20',
    monetize: false,
  };
}

export function emptyNotificationForm() {
  return {
    name: '',
    description: '',
    priceAmount: '',
    priceVatRate: '20',
    questions: [
      {
        type: 'text',
        questionName: '',
        textKind: 'short',
        addValueToTotalTTC: false,
        required: true,
        optionsSelectable: 1,
        options: [emptyOption()],
      },
    ],
  };
}

function optionFromApi(o) {
  const priceHT = o?.priceHT;
  const priceTTC = o?.priceTTC ?? o?.price;
  const hasPrice =
    (priceHT != null && priceHT !== '') ||
    (priceTTC != null && priceTTC !== '');
  const { priceAmount, priceVatRate } = priceFieldsFromApi(priceHT, priceTTC, null);
  return {
    optionName: String(o?.optionName ?? ''),
    priceAmount,
    priceVatRate,
    monetize: hasPrice,
  };
}

export function notificationFormFromApi(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const name = String(raw.name ?? '').trim();
  if (!name) return null;
  const basePrice = priceFieldsFromApi(raw.priceHT, raw.priceTTC, raw.price);
  const questions = Array.isArray(raw.questions)
    ? raw.questions.map((q) => {
        const type =
          q?.type === 'options'
            ? 'options'
            : q?.type === 'note'
              ? 'note'
              : 'text';
        const questionName = String(q?.questionName ?? '');
        const required = parseQuestionRequired(q?.required);
        if (type === 'note') {
          return {
            type: 'note',
            questionName,
            required,
            optionsSelectable: 1,
            options: [],
          };
        }
        if (type === 'text') {
          const textKind = parseTextKind(q?.textKind);
          return {
            type: 'text',
            questionName,
            textKind,
            addValueToTotalTTC: questionAddsValueToTotalTTC({
              type: 'text',
              textKind,
              addValueToTotalTTC: q?.addValueToTotalTTC,
            }),
            required,
            optionsSelectable: 1,
            options: [],
          };
        }
        const options = Array.isArray(q?.options)
          ? q.options.map(optionFromApi)
          : [emptyOption()];
        return {
          type: 'options',
          questionName,
          required,
          optionsSelectable: Math.max(1, Number(q?.optionsSelectable) || 1),
          options: options.length ? options : [emptyOption()],
        };
      })
    : [];
  return {
    name,
    description: String(raw.description ?? ''),
    priceAmount: basePrice.priceAmount,
    priceVatRate: basePrice.priceVatRate,
    questions: questions.length
      ? questions
      : emptyNotificationForm().questions,
  };
}

export function validateNotificationFormDraft(form) {
  if (!form) return null;
  const name = String(form.name ?? '').trim();
  if (!name) return 'Le nom du formulaire est requis.';
  const questions = Array.isArray(form.questions) ? form.questions : [];
  if (!questions.length) return 'Ajoutez au moins un champ.';
  for (let i = 0; i < questions.length; i += 1) {
    const q = questions[i];
    const label = String(q?.questionName ?? '').trim();
    if (!label) return `Le champ ${i + 1} doit avoir un intitulé.`;
    if (q.type === 'options') {
      const opts = Array.isArray(q.options) ? q.options : [];
      const filled = opts.filter((o) => String(o?.optionName ?? '').trim());
      if (!filled.length) {
        return `Le champ « ${label} » doit avoir au moins une option.`;
      }
      const sel = Math.max(1, Number(q.optionsSelectable) || 1);
      if (sel > filled.length) {
        return `Le champ « ${label} » : le maximum de cases cochables ne peut pas dépasser le nombre d’options.`;
      }
      for (const opt of filled) {
        if (opt.monetize === true && String(opt.priceAmount ?? '').trim()) {
          const { priceHT } = computePricePair(opt.priceAmount, opt.priceVatRate);
          if (priceHT == null) {
            return `Le champ « ${label} » : prix d’option invalide.`;
          }
        }
      }
    }
  }
  if (String(form.priceAmount ?? '').trim()) {
    const { priceHT } = computePricePair(form.priceAmount, form.priceVatRate);
    if (priceHT == null) {
      return 'Le prix de base HT doit être un nombre positif ou vide.';
    }
  }
  return null;
}

function serializeOption(o) {
  const optionName = String(o.optionName ?? '').trim();
  if (!optionName) return null;
  if (o.monetize !== true || !String(o.priceAmount ?? '').trim()) {
    return { optionName, priceHT: null, priceTTC: null };
  }
  const { priceHT, priceTTC } = computePricePair(o.priceAmount, o.priceVatRate);
  if (priceHT == null || priceTTC == null) return null;
  return { optionName, priceHT, priceTTC };
}

export function serializeNotificationForm(form) {
  const name = String(form.name ?? '').trim();
  const questions = (Array.isArray(form.questions) ? form.questions : [])
    .map((q) => {
      const questionName = String(q.questionName ?? '').trim();
      if (!questionName) return null;
      const required = parseQuestionRequired(q.required);
      if (q.type === 'options') {
        const options = (Array.isArray(q.options) ? q.options : [])
          .map(serializeOption)
          .filter(Boolean);
        if (!options.length) return null;
        return {
          type: 'options',
          questionName,
          required,
          optionsSelectable: Math.min(
            Math.max(1, Number(q.optionsSelectable) || 1),
            options.length,
          ),
          options,
        };
      }
      if (q.type === 'note') {
        return {
          type: 'note',
          questionName,
          required,
        };
      }
      const textKind = parseTextKind(q.textKind);
      const addValueToTotalTTC = questionAddsValueToTotalTTC({
        type: 'text',
        textKind,
        addValueToTotalTTC: q.addValueToTotalTTC,
      });
      return {
        type: 'text',
        questionName,
        textKind,
        required,
        ...(addValueToTotalTTC ? { addValueToTotalTTC: true } : {}),
      };
    })
    .filter(Boolean);

  const base = computePricePair(form.priceAmount, form.priceVatRate);
  const hasBase = String(form.priceAmount ?? '').trim() !== '';

  return {
    name,
    description: String(form.description ?? '').trim(),
    priceHT: hasBase ? base.priceHT : null,
    priceTTC: hasBase ? base.priceTTC : null,
    questions,
  };
}

export function buildNotificationFormPayload(formEnabled, linkedForm, hadFormBefore) {
  if (!formEnabled || !linkedForm) {
    if (hadFormBefore) return { ok: true, payload: { form: null } };
    return { ok: true, payload: {} };
  }
  const serialized = serializeNotificationForm(linkedForm);
  if (!serialized.name || !serialized.questions.length) {
    return {
      ok: false,
      error:
        'Le formulaire est incomplet. Vérifiez le nom et au moins un champ valide, puis validez le formulaire.',
    };
  }
  return { ok: true, payload: { form: serialized } };
}

export function textKindLabel(textKind) {
  if (textKind === 'long') return 'Texte long';
  if (textKind === 'number') return 'Nombre';
  if (textKind === 'email') return 'E-mail';
  if (textKind === 'date') return 'Date';
  if (textKind === 'phone') return 'Téléphone';
  return 'Texte court';
}

export function questionColumnLabel(q, index) {
  const name = String(q?.questionName ?? '').trim() || `Champ ${index + 1}`;
  const required = parseQuestionRequired(q?.required);
  const optionalSuffix = required ? '' : ' (facultatif)';
  if (q?.type === 'options') {
    const max = Math.max(1, Number(q?.optionsSelectable) || 1);
    return `${name}${optionalSuffix} · max ${max}`;
  }
  if (q?.type === 'note') {
    return `${name}${optionalSuffix} · note 1-5`;
  }
  const amountSuffix = questionAddsValueToTotalTTC(q) ? ' · montant TTC' : '';
  const kindSuffix =
    q?.textKind === 'long'
      ? ' · texte long'
      : q?.textKind === 'number'
        ? ' · nombre'
        : q?.textKind === 'email'
          ? ' · e-mail'
          : q?.textKind === 'date'
            ? ' · date'
            : q?.textKind === 'phone'
              ? ' · téléphone'
              : '';
  return `${name}${kindSuffix}${amountSuffix}${optionalSuffix}`;
}

export const FORM_TEXT_KIND_OPTIONS = [
  { value: 'short', label: 'Texte court (une ligne)' },
  { value: 'long', label: 'Texte long (zone multiligne)' },
  { value: 'number', label: 'Nombre' },
  { value: 'email', label: 'E-mail' },
  { value: 'date', label: 'Date' },
  { value: 'phone', label: 'Téléphone' },
];

/** Payload API HubUP (`form` → `formDraft` sur NotificationSchedule). */
export function resolveNotificationFormPayload(formEnabled, linkedForm, hadFormBefore) {
  const built = buildNotificationFormPayload(formEnabled, linkedForm, hadFormBefore);
  if (!built.ok) return { error: built.error };
  return { payload: built.payload };
}

export function formatTtcPreview(htAmount, vatRateStr) {
  const { priceTTC } = computePricePair(htAmount, vatRateStr);
  if (priceTTC == null) return null;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(priceTTC);
}
